import React, { useState } from 'react';
import { useLanguage } from '@/hooks/use-language';
import { useGroups } from '@/hooks/use-groups';
import { useSupabase } from '@/contexts/supabase';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CalendarIcon, Repeat, Info } from 'lucide-react';
import { format, addDays, differenceInWeeks, differenceInMonths } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface CreateEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEventCreated: () => void;
}

export function CreateEventDialog({ open, onOpenChange, onEventCreated }: CreateEventDialogProps) {
  const { t } = useLanguage();
  const supabase = useSupabase();
  const { allGroups, isLoading: groupsLoading } = useGroups();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventDate: new Date(),
    eventTime: '18:00',
    location: 'Gløshaugen',
    groupId: '',
    level: '',
    maxParticipants: 20,
    eventType: 'training',
    notes: '',
    customSignupHours: undefined as number | undefined,
    recurrencePattern: 'none',
    recurrenceEndDate: addDays(new Date(), 90),
  });

  // Debug logging
  React.useEffect(() => {
    console.log('[CreateEventDialog] Component mounted/opened', {
      open,
      groupsLoading,
      allGroupsCount: allGroups.length,
      allGroups: allGroups.map(g => ({ id: g.id, name: g.name }))
    });
  }, [open, allGroups, groupsLoading]);

  // Debug dropdown interactions
  React.useEffect(() => {
    console.log('[CreateEventDialog] Form data updated:', {
      groupId: formData.groupId,
      eventType: formData.eventType,
      recurrencePattern: formData.recurrencePattern,
    });
  }, [formData.groupId, formData.eventType, formData.recurrencePattern]);

  // Calculate number of events that will be created
  const calculateEventCount = () => {
    if (formData.recurrencePattern === 'none') return 1;
    
    const start = formData.eventDate;
    const end = formData.recurrenceEndDate;
    
    switch (formData.recurrencePattern) {
      case 'weekly':
        return Math.floor(differenceInWeeks(end, start)) + 1;
      case 'biweekly':
        return Math.floor(differenceInWeeks(end, start) / 2) + 1;
      case 'monthly':
        return differenceInMonths(end, start) + 1;
      default:
        return 1;
    }
  };

  const handleCreate = async () => {
    console.log('[CreateEventDialog] handleCreate called', {
      formData: {
        title: formData.title,
        groupId: formData.groupId,
        eventDate: formData.eventDate,
        recurrencePattern: formData.recurrencePattern,
      }
    });
    
    if (!formData.title || !formData.groupId) {
      console.log('[CreateEventDialog] Validation failed: missing title or group');
      toast.error('Title and group are required');
      return;
    }
    
    setLoading(true);
    try {
      if (formData.recurrencePattern !== 'none') {
        // Create recurring series via edge function
        const { data, error } = await supabase.functions.invoke('manage-event', {
          body: {
            action: 'create_recurring_series',
            title: formData.title,
            description: formData.description,
            event_date: formData.eventDate.toISOString().split('T')[0],
            event_time: formData.eventTime,
            location: formData.location,
            group_id: formData.groupId,
            level: formData.level || null,
            max_participants: formData.maxParticipants,
            event_type: formData.eventType,
            notes: formData.notes || null,
            custom_signup_hours: formData.customSignupHours || null,
            recurrence_pattern: formData.recurrencePattern,
            recurrence_end_date: formData.recurrenceEndDate.toISOString().split('T')[0],
          },
        });

        if (error) throw error;
        if (!data?.success) throw new Error(data?.error || 'Failed to create recurring series');

        toast.success(`${t('admin.event_created')} (${data.event_count} events)`);
      } else {
        // Create single event
        const { data, error } = await supabase.functions.invoke('manage-event', {
          body: {
            action: 'create_event',
            title: formData.title,
            description: formData.description,
            event_date: formData.eventDate.toISOString().split('T')[0],
            event_time: formData.eventTime,
            location: formData.location,
            group_id: formData.groupId,
            level: formData.level || null,
            max_participants: formData.maxParticipants,
            event_type: formData.eventType,
            notes: formData.notes || null,
            custom_signup_hours: formData.customSignupHours || null,
          },
        });

        if (error) throw error;
        if (!data?.success) throw new Error(data?.error || 'Failed to create event');

        toast.success(t('admin.event_created'));
      }

      onEventCreated();
      onOpenChange(false);
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        eventDate: new Date(),
        eventTime: '18:00',
        location: 'Gløshaugen',
        groupId: '',
        level: '',
        maxParticipants: 20,
        eventType: 'training',
        notes: '',
        customSignupHours: undefined,
        recurrencePattern: 'none',
        recurrenceEndDate: addDays(new Date(), 90),
      });
    } catch (error: any) {
      console.error('[CreateEventDialog] Error creating event:', {
        error,
        errorName: error?.name,
        errorMessage: error?.message,
        errorContext: error?.context,
        formData: {
          title: formData.title,
          eventDate: formData.eventDate,
          groupId: formData.groupId,
          recurrencePattern: formData.recurrencePattern,
        }
      });
      toast.error(`Failed to create event: ${error?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            {t('admin.create_event')}
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 space-y-4 pr-2">
          {/* Title */}
          <div>
            <Label>{t('event.title')}</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Weekly Training"
            />
          </div>

          {/* Description */}
          <div>
            <Label>{t('event.description')}</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              placeholder="Training session for intermediate players"
            />
          </div>

          {/* Event Type */}
          <div>
            <Label>{t('event.type')}</Label>
            <Select 
              value={formData.eventType} 
              onValueChange={(value) => {
                console.log('[CreateEventDialog] Event type selected:', value);
                setFormData({ ...formData, eventType: value });
              }}
              onOpenChange={(open) => console.log('[CreateEventDialog] Event type dropdown open state:', open)}
            >
              <SelectTrigger onClick={() => console.log('[CreateEventDialog] Event type dropdown clicked')}>
                <SelectValue placeholder={t('event.type')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="training">{t('event.type.training')}</SelectItem>
                <SelectItem value="tournament">{t('event.type.tournament')}</SelectItem>
                <SelectItem value="social">{t('event.type.social')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('event.date')}</Label>
              <Popover onOpenChange={(open) => console.log('[CreateEventDialog] Event date popover open state:', open)}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-left font-normal"
                    onClick={() => console.log('[CreateEventDialog] Event date button clicked')}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(formData.eventDate, "PPP")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.eventDate}
                    defaultMonth={formData.eventDate}
                    onSelect={(date) => {
                      console.log('[CreateEventDialog] Event date selected:', date);
                      if (date) {
                        setFormData({ ...formData, eventDate: date });
                      }
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label>{t('event.time')}</Label>
              <Input
                type="time"
                value={formData.eventTime}
                onChange={(e) => setFormData({ ...formData, eventTime: e.target.value })}
              />
            </div>
          </div>

          {/* Location */}
          <div>
            <Label>{t('event.location')}</Label>
            <Input
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            />
          </div>

          {/* Group/Subgroup */}
          <div>
            <Label>{t('group.select')}</Label>
            <Select 
              value={formData.groupId} 
              onValueChange={(value) => {
                console.log('[CreateEventDialog] Group selected:', value);
                setFormData({ ...formData, groupId: value });
              }}
              onOpenChange={(open) => console.log('[CreateEventDialog] Group dropdown open state:', open, { allGroups: allGroups.length })}
            >
              <SelectTrigger onClick={() => console.log('[CreateEventDialog] Group dropdown clicked', { allGroups })}>
                <SelectValue placeholder={t('group.select')} />
              </SelectTrigger>
              <SelectContent>
                {allGroups.map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Level and Max Participants */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('event.level')}</Label>
              <Input
                value={formData.level}
                onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                placeholder="Beginner, Intermediate, Advanced"
              />
            </div>

            <div>
              <Label>{t('event.max_participants')}</Label>
              <Input
                type="number"
                value={formData.maxParticipants}
                onChange={(e) => setFormData({ ...formData, maxParticipants: parseInt(e.target.value) })}
              />
            </div>
          </div>

          {/* Custom Signup Window */}
          <div>
            <Label>{t('admin.custom_signup_window')}</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={formData.customSignupHours || ''}
                placeholder="72"
                onChange={(e) => setFormData({ 
                  ...formData, 
                  customSignupHours: e.target.value ? parseInt(e.target.value) : undefined 
                })}
              />
              <span className="text-sm text-muted-foreground">hours</span>
            </div>
          </div>

          {/* Recurrence */}
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-center gap-2">
              <Repeat className="h-4 w-4" />
              <Label>{t('admin.recurrence_pattern')}</Label>
            </div>
            <Select 
              value={formData.recurrencePattern} 
              onValueChange={(value) => {
                console.log('[CreateEventDialog] Recurrence pattern selected:', value);
                setFormData({ ...formData, recurrencePattern: value });
              }}
              onOpenChange={(open) => console.log('[CreateEventDialog] Recurrence dropdown open state:', open)}
            >
              <SelectTrigger onClick={() => console.log('[CreateEventDialog] Recurrence dropdown clicked')}>
                <SelectValue placeholder={t('admin.recurrence_pattern')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('admin.recurrence.none')}</SelectItem>
                <SelectItem value="weekly">{t('admin.recurrence.weekly')}</SelectItem>
                <SelectItem value="biweekly">{t('admin.recurrence.biweekly')}</SelectItem>
                <SelectItem value="monthly">{t('admin.recurrence.monthly')}</SelectItem>
              </SelectContent>
            </Select>

            {formData.recurrencePattern !== 'none' && (
              <>
                <div>
                  <Label>{t('admin.recurrence_end_date')}</Label>
                  <Popover onOpenChange={(open) => console.log('[CreateEventDialog] End date popover open state:', open)}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-left font-normal"
                        onClick={() => console.log('[CreateEventDialog] End date button clicked')}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(formData.recurrenceEndDate, "PPP")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.recurrenceEndDate}
                        defaultMonth={formData.recurrenceEndDate}
                        onSelect={(date) => {
                          console.log('[CreateEventDialog] End date selected:', date);
                          if (date) {
                            setFormData({ ...formData, recurrenceEndDate: date });
                          }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Event Count Preview */}
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    <div className="font-medium">
                      {t('admin.will_create_events', { count: calculateEventCount() })}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      From {format(formData.eventDate, 'PPP')} to {format(formData.recurrenceEndDate, 'PPP')}
                    </div>
                  </AlertDescription>
                </Alert>
              </>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label>{t('event.notes')}</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              placeholder="Additional notes..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleCreate} disabled={loading}>
            {loading ? t('common.saving') : t('admin.create_event')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
